'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Sparkles, MessageCircle, ArrowRight, ChevronRight } from 'lucide-react';

const sampleQuestions = [
  'Which pooja should I do for a new business?',
  'What ritual helps with ancestral blessings?',
  'Best ceremony before buying a house?',
  'Remedy for Shani dosha in horoscope?',
];

export default function AIAssistantBanner() {
  const [activeQ, setActiveQ] = useState<number | null>(null);

  return (
    <section className="py-12 px-4 sm:px-6">
      <div className="max-w-screen-xl mx-auto">
        <div
          className="relative rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #500A0A 0%, #7B1C1C 40%, #9B3A1A 80%, #C4956A 100%)',
          }}
        >
          {/* Background mandala */}
          <div
            className="absolute right-0 top-0 w-80 h-80 opacity-10 animate-rotate-slow"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 300 300' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='150' cy='150' r='140' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='150' cy='150' r='110' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='150' cy='150' r='80' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='150' cy='150' r='50' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3C/svg%3E")`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
            }}
          />
          <div className="absolute top-0 left-0 w-64 h-64 bg-saffron/10 rounded-full blur-3xl" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8 p-8 lg:p-12">
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/40 text-gold text-xs font-medium px-4 py-1.5 rounded-full mb-5 tracking-wider">
                <Sparkles size={12} />
                Yagnick AI — Your Spiritual Guide
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-ivory mb-4 leading-tight">
                Not sure which ritual
                <br />
                <span className="text-gold">you need?</span>
              </h2>
              <p className="text-ivory/70 text-base mb-6 font-body leading-relaxed max-w-md">
                Our AI understands your life situation, astrological needs, and family traditions to recommend the most appropriate Vedic ritual — with full explanation.
              </p>
              <Link
                href="/ai-guide"
                className="inline-flex items-center gap-3 saffron-gradient text-white font-semibold px-8 py-4 rounded-2xl btn-glow text-base"
              >
                <MessageCircle size={20} />
                Ask Yagnick AI
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Right — Chat Preview */}
            <div className="flex-1 w-full max-w-md">
              <div className="glass-dark rounded-2xl p-5 border border-gold/30">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gold/20">
                  <div className="w-9 h-9 rounded-full saffron-gradient flex items-center justify-center">
                    <span className="text-lg">🪔</span>
                  </div>
                  <div>
                    <div className="text-ivory text-sm font-semibold">Yagnick AI</div>
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-ivory/50 text-xs">Always available</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="bg-gold/10 border border-gold/20 rounded-xl rounded-tl-sm px-4 py-3">
                    <p className="text-ivory/90 text-sm font-body">
                      🙏 Namaste! I can suggest the perfect ritual for your needs. What brings you here today?
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {sampleQuestions?.map((q, i) => (
                    <button
                      key={i}
                      onMouseEnter={() => setActiveQ(i)}
                      onMouseLeave={() => setActiveQ(null)}
                      className={`w-full flex items-center gap-2 text-left px-4 py-2.5 rounded-xl border text-xs font-body transition-all duration-200 ${
                        activeQ === i
                          ? 'bg-saffron/20 border-saffron/40 text-saffron' :'bg-white/5 border-white/10 text-ivory/70 hover:bg-white/10'
                      }`}
                    >
                      <ChevronRight size={13} className={activeQ === i ? 'text-saffron' : 'text-ivory/40'} />
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}